import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner, Modal } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import InterestRateHttpService from '../../../../../services/http/interest-rate-http';
import handleResponseError from '../../../../../utils/handleResponseError';

interface filters {
    paymentMethod: any;
    installment: number;
    percentage: number;
}

interface InterestRateModalProps {
    isEdit: boolean;
    selectedRate: any;
    update: () => void;
    isOpen: (value: boolean) => void;
    onHide: () => void;
    show: boolean;
    paymentMethods: any;
}

const InterestRateModal = ({
    isEdit,
    selectedRate,
    update,
    isOpen,
    onHide,
    show,
    paymentMethods
}: InterestRateModalProps) => {
    const [loading, setLoading] = useState(false);
    const {
        control: formControl,
        watch,
        reset,
        setValue,
    } = useForm<filters>({
        shouldUnregister: false,
        defaultValues: {
            paymentMethod: '',
            installment: 1,
            percentage: 0,
        },
    });

    const fieldsData = watch();

    const createNewRate = async () => {
        setLoading(true);
        try {
            const payload = {
                paymentMethod: { id: Number(fieldsData.paymentMethod) },
                installment: Number(fieldsData.installment),
                percentage: Number(fieldsData.percentage),
            };
            const response = await InterestRateHttpService.saveInterestRate(payload);
            if (response) {
                update();
                toast.success('Método de pagamento salvo com sucesso!');
            }
        } catch (err: any) {
            handleResponseError(err, 'Ocorreu um erro ao salvar método de pagamento.');
        } finally {
            setLoading(false);
        }
    };


    const editRate = async () => {
        setLoading(true);
        try {
            const payload = {
                id: selectedRate.id,
                paymentMethod: { id: fieldsData.paymentMethod },
                installment: Number(fieldsData.installment),
                percentage: Number(fieldsData.percentage)
            };
            const response = await InterestRateHttpService.editInterestRate(payload);
            if (response) {
                update();
                toast.success('Método de pagamento salvo com sucesso!');
            }
        } catch (err: any) {
            handleResponseError(err, 'Ocorreu um erro ao salvar método de pagamento.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedRate) {
            setValue('paymentMethod', selectedRate.paymentMethod?.id || 0);
            setValue('installment', selectedRate.installment || 1);
            setValue('percentage', selectedRate.percentage || 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRate, show]);

    useEffect(() => {
        if (!selectedRate) {
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    return (
        <Modal
            className="modal-dialog-scrollable"
            show={show}
            onHide={onHide}
            size="lg"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title className="ml-3 mr-3" id="contained-modal-title-vcenter">
                    {isEdit ? 'Editar método de pagamento' : 'Criar novo método de pagamento'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="w-100">
                <Form.Group controlId="formInterestRatePaymentMethod">
                    <Form.Label>Nome do meio de pagamento:</Form.Label>
                    <Controller
                        render={({ onChange, value }) => (
                            <Form.Control
                                as="select"
                                value={value}
                                onChange={onChange}
                            >
                                <option value="">Selecione...</option>
                                {paymentMethods.map((pm: any) => (
                                    <option key={pm.id} value={pm.id}>{pm.name}</option>
                                ))}
                            </Form.Control>
                        )}
                        rules={{ required: 'Campo obrigatório' }}
                        control={formControl}
                        name="paymentMethod"
                        defaultValue=''
                    />
                </Form.Group>
                <Form.Group controlId="formInterestRateInstallment">
                    <Form.Label>Quantidade de Parcelas:</Form.Label>
                    <Controller
                        render={({ onChange, value }) => (
                            <Form.Control
                                type="number"
                                min={1}
                                value={value}
                                onChange={onChange}
                            />
                        )}
                        rules={{ required: 'Campo obrigatório' }}
                        control={formControl}
                        name="installment"
                        defaultValue={1}
                    />
                </Form.Group>
                <Form.Group controlId="formInterestRatePercentage">
                    <Form.Label>Taxa de Juros (%):</Form.Label>
                    <Controller
                        render={({ onChange, value }) => (
                            <Form.Control
                                type="number"
                                min={0}
                                step={0.01}
                                value={value}
                                onChange={onChange}
                            />
                        )}
                        rules={{ required: 'Campo obrigatório' }}
                        control={formControl}
                        name="percentage"
                        defaultValue={0}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer className="ml-3 mr-3 mb-2">
                <Button
                    variant="outline-primary"
                    style={{ width: '150px' }}
                    onClick={() => {
                        reset();
                        isOpen(false);
                        onHide();
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    style={{ width: '150px' }}
                    onClick={isEdit ? editRate : createNewRate}
                >
                    {loading ? <Spinner animation="border" /> : 'Salvar método de pagamento'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default InterestRateModal;
